import { Injectable, InjectionToken, inject, signal } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
  NOTO_ICON_NAMES_LOADER,
  NOTO_ICON_SHARD_BOUNDS,
  NOTO_ICON_SHARD_LOADERS,
} from '../icons/noto/noto-icon-shards.generated';

/**
 * Where the generated Noto data comes from. The artwork is split by sorted name into
 * shards of roughly half a megabyte, each its own lazy chunk, so a reflection screen
 * loads only the shards its handful of emoji live in rather than the whole ~27 MB set.
 * Behind a token so specs swap in a small fixture instead of the generated modules.
 */
export interface NotoIconSource {
  /** First name of every shard, in order: a name lives in the last shard whose first name sorts at or before it. */
  readonly shardBounds: readonly string[];
  /** Resolves to one shard's JSON, a map from icon name to SVG body. */
  loadShard(index: number): Promise<string>;
  /** Resolves to the JSON array of every icon name, for a picker browsing the set. */
  loadNames(): Promise<string>;
}

export const NOTO_ICON_SOURCE = new InjectionToken<NotoIconSource>('NOTO_ICON_SOURCE', {
  providedIn: 'root',
  factory: () => ({
    shardBounds: NOTO_ICON_SHARD_BOUNDS,
    loadShard: (index) => NOTO_ICON_SHARD_LOADERS[index](),
    loadNames: NOTO_ICON_NAMES_LOADER,
  }),
});

export type NotoIconsStatus = 'idle' | 'loading' | 'ready' | 'error';

/**
 * Resolves Noto emoji to renderable SVG, loading icon data shard by shard on first use.
 *
 * Apps consume this library from source, so each app's build code-splits the dynamic
 * imports normally. On a Capacitor build the chunks already sit on the device, so the
 * wait is a file read and a parse rather than a network round trip.
 */
@Injectable({ providedIn: 'root' })
export class NotoIconsService {
  private readonly source = inject(NOTO_ICON_SOURCE);
  private readonly sanitizer = inject(DomSanitizer);

  /** Every body loaded so far, across shards. */
  private readonly bodies = signal<Readonly<Record<string, string>>>({});
  private readonly pendingShards = new Map<number, Promise<void>>();
  private pendingNames?: Promise<void>;
  private readonly trusted = new Map<string, SafeHtml>();

  /** Every icon name once {@link loadNames} has resolved; the catalogue a picker browses. */
  readonly names = signal<readonly string[]>([]);
  readonly namesStatus = signal<NotoIconsStatus>('idle');

  /**
   * Loads the shard holding `name`. Idempotent per shard, and concurrent callers share
   * one load. A failed load is forgotten rather than cached, so the next request for
   * that shard tries again: the realistic failures are a stale index.html asking for a
   * chunk hash a deploy has replaced, and a mobile network that comes back.
   */
  load(name: string): Promise<void> {
    const index = this.shardOf(name);
    const pending = this.pendingShards.get(index);
    if (pending) return pending;

    const loading = Promise.resolve()
      .then(() => this.source.loadShard(index))
      .then((json) => {
        const shard = JSON.parse(json) as Record<string, string>;
        this.bodies.update((all) => ({ ...all, ...shard }));
      })
      .catch((error: unknown) => {
        // Deliberately swallowed: the icon degrades to its reserved empty box, which is
        // the right outcome for a missing glyph and not worth an unhandled rejection in
        // Sentry.
        this.pendingShards.delete(index);
        console.error(`Failed to load Noto icon data (shard ${index})`, error);
      });
    this.pendingShards.set(index, loading);
    return loading;
  }

  /** Loads the full name list. Same sharing and retry semantics as {@link load}. */
  loadNames(): Promise<void> {
    if (this.pendingNames) return this.pendingNames;

    this.namesStatus.set('loading');
    this.pendingNames = Promise.resolve()
      .then(() => this.source.loadNames())
      .then((json) => {
        this.names.set(JSON.parse(json) as string[]);
        this.namesStatus.set('ready');
      })
      .catch((error: unknown) => {
        this.pendingNames = undefined;
        this.namesStatus.set('error');
        console.error('Failed to load the Noto icon name list', error);
      });
    return this.pendingNames;
  }

  /**
   * Renderable SVG for a Noto icon name, or null until its shard has loaded. Reads a
   * signal, so a caller in a template re-evaluates once the load completes.
   */
  svg(name: string): SafeHtml | null {
    const cached = this.trusted.get(name);
    if (cached) return cached;

    const body = this.bodies()[name];
    if (!body) return null;

    // Trusted once per icon and cached: a fresh SafeHtml identity on every change
    // detection pass would make Angular reassign innerHTML and reparse the SVG.
    // The markup is build-time data from a generator that rejects scripts, event
    // handlers and external references -- never user input.
    const html = this.sanitizer.bypassSecurityTrustHtml(
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 128 128" width="1em" height="1em" focusable="false" aria-hidden="true">${body}</svg>`,
    );
    this.trusted.set(name, html);
    return html;
  }

  /** Binary search over the shard bounds; anything sorting before the first bound maps to shard 0. */
  private shardOf(name: string): number {
    const bounds = this.source.shardBounds;
    let low = 0;
    let high = bounds.length - 1;
    while (low < high) {
      const mid = (low + high + 1) >> 1;
      if (bounds[mid] <= name) low = mid;
      else high = mid - 1;
    }
    return low;
  }
}
